import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { LANGUAGES } from "../i18n/translations";

const DISMISS_KEY = "sf_lang_hint";
const CODES = LANGUAGES.map((l) => l.code);

const COPY = {
  en: { text: "This site is available in English.", accept: "Continue in English", dismiss: "No, thanks" },
  tr: { text: "Bu site Türkçe olarak da mevcut.", accept: "Türkçe devam et", dismiss: "Hayır, teşekkürler" },
  ar: { text: "هذا الموقع متوفر بالعربية.", accept: "المتابعة بالعربية", dismiss: "لا، شكراً" },
};

const deviceLanguage = () => {
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language || ""];
  for (const candidate of candidates) {
    const code = String(candidate).toLowerCase().split("-")[0];
    if (CODES.includes(code)) return code;
  }
  return null;
};

export const LanguageSuggestion = () => {
  const { lang, setLang } = useLang();
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY)) return;
    const device = deviceLanguage();
    setSuggestion(device && device !== lang ? device : null);
  }, [lang]);

  const close = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setSuggestion(null);
  };

  const accept = () => {
    setLang(suggestion);
    close();
  };

  const copy = COPY[suggestion];

  return createPortal(
    <AnimatePresence>
      {suggestion && copy && (
        <div className="fixed top-[84px] sm:top-[100px] inset-x-4 z-[55] flex justify-center pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: -14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -14 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          dir={suggestion === "ar" ? "rtl" : "ltr"}
          data-testid="language-suggestion"
          className="w-full sm:w-auto max-w-[560px] pointer-events-auto"
        >
          <div className="flex items-center gap-2 sm:gap-3 rounded-2xl sm:rounded-full bg-charcoal/95 backdrop-blur border border-white/15 shadow-[0_18px_45px_rgba(0,0,0,0.35)] px-3 sm:px-4 py-2.5">
            <Globe className="w-4 h-4 text-gold shrink-0" />
            <span className="text-white/85 text-[11px] sm:text-sm flex-1 min-w-0">{copy.text}</span>
            <button
              onClick={accept}
              data-testid="language-suggestion-accept"
              className="shrink-0 rounded-full bg-gold text-charcoal text-[11px] sm:text-xs font-semibold px-3.5 py-1.5 hover:bg-gold-soft transition-colors"
            >
              {copy.accept}
            </button>
            <button
              onClick={close}
              aria-label={copy.dismiss}
              title={copy.dismiss}
              data-testid="language-suggestion-dismiss"
              className="shrink-0 w-7 h-7 rounded-full text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};
